import React, { memo } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEdit,
    faTrash,
    faInfoCircle,
    faChartLine,
    faClipboardList,
    faEnvelope,
    faTableColumns,
} from "@fortawesome/free-solid-svg-icons";
import { Dropdown } from "react-bootstrap";

import { AppUserApi, ClinicApi } from "../../api";
import { useAppNavigate } from "../../Router/hook";
import { useAppSelector, useAppDispatch } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user/selector";
import { setWeek } from "../../redux-config/entities/week";
import { SendNotification } from "../../components";

interface UserActionsProps {
    userId: number;
    userName: string;
    userEmail?: string | null;
    userPhone?: string | null;
    patientProfileId?: number | null;
    isJoined?: boolean;
    fetchList: () => void;
}

const UserActionsDropdown: React.FC<UserActionsProps> = ({
    userId,
    userName,
    userEmail,
    userPhone,
    patientProfileId,
    isJoined = true,
    fetchList,
}) => {
    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();

    const handleSendInvitation = () => {
        if (!patientProfileId) return;
        ClinicApi.SendClinicPatientInvitation(user, patientProfileId)
            .then(() => {
                Swal.fire({
                    icon: "success",
                    text: "Invitation sent successfully.",
                });
                fetchList();
            })
            .catch((err) =>
                Swal.fire({
                    icon: "error",
                    text: err?.message ?? "Failed to send invitation.",
                }),
            );
    };

    const handleDelete = () => {
        Swal.fire({
            text: `Are you sure to delete ${userName}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
        }).then((result) => {
            if (result.isConfirmed) {
                AppUserApi.DeleteUser(user, userId)
                    .then(() => fetchList())
                    .catch((err) => Swal.fire({ icon: "error", text: err }));
            }
        });
    };

    const handleReleaseLoginIdentifiers = () => {
        const options: Record<string, string> = {};
        if (userEmail) options.email = `Email — ${userEmail}`;
        if (userPhone) options.phone = `Phone — ${userPhone}`;
        if (userEmail && userPhone) options.both = "Email and phone";

        Swal.fire({
            title: "Release login details?",
            text: "The original values will be backed up, removed from this account, and made available for a new user. This account will be deactivated.",
            icon: "warning",
            input: "select",
            inputOptions: options,
            inputPlaceholder: "Select what to release",
            showCancelButton: true,
            confirmButtonText: "Back up and release",
            confirmButtonColor: "#b54708",
            inputValidator: (value) =>
                value ? null : "Select email, phone, or both.",
        }).then((result) => {
            if (!result.isConfirmed) return;

            const fields: Array<"email" | "phone"> =
                result.value === "both"
                    ? ["email", "phone"]
                    : [result.value as "email" | "phone"];

            AppUserApi.ReleaseUserLoginIdentifiers(user, userId, fields)
                .then(() => {
                    Swal.fire({
                        icon: "success",
                        title: "Login details released",
                        text: "The original values are backed up and can now be used for a new user.",
                    });
                    fetchList();
                })
                .catch((err) =>
                    Swal.fire({
                        icon: "error",
                        text:
                            err?.data?.message ??
                            err?.message ??
                            "Could not release login details.",
                    }),
                );
        });
    };

    const fetchPlanWeekList = () => {
        AppUserApi.ListPlanUserWeek(user, userId, false)
            .then((data) => {
                const currentWeek = data.plan.find(
                    (p: any) => p.isCurrentWeek,
                ) || {
                    index: 0,
                    endDate: "",
                    id: 0,
                    isCurrentWeek: false,
                    startDate: "",
                    title: "",
                };
                dispatch(setWeek(currentWeek));
                navigate(`/app-user/${userId}/progress/detail`);
            })
            .catch((err) => Swal.fire({ icon: "error", text: err }));
    };

    return (
        <Dropdown align="end" className="app-user-actions-dropdown">
            <Dropdown.Toggle
                size="sm"
                variant="light"
                className="app-user-actions-toggle"
            >
                Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {!isJoined && patientProfileId ? (
                    <>
                        <Dropdown.Item onClick={handleSendInvitation}>
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="me-1"
                            />{" "}
                            Send Invitation
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            className="text-danger"
                            onClick={handleDelete}
                        >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />{" "}
                            Delete
                        </Dropdown.Item>
                    </>
                ) : (
                    <>
                        <Dropdown.Item
                            as={Link}
                            to={`/app-user/info/${userId}`}
                        >
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className="me-1"
                            />{" "}
                            Info
                        </Dropdown.Item>
                        <Dropdown.Item
                            as={Link}
                            to={`/app-user/edit/${userId}`}
                        >
                            <FontAwesomeIcon icon={faEdit} className="me-1" />{" "}
                            Edit
                        </Dropdown.Item>
                        <Dropdown.Item onClick={fetchPlanWeekList}>
                            <FontAwesomeIcon
                                icon={faChartLine}
                                className="me-1"
                            />{" "}
                            Progress Detail
                        </Dropdown.Item>
                        <Dropdown.Item
                            as={Link}
                            to={`/app-user/${userId}/progress/overview?type=engagement_detail&period=3m`}
                        >
                            <FontAwesomeIcon
                                icon={faChartLine}
                                className="me-1"
                            />{" "}
                            Progress Overview
                        </Dropdown.Item>
                        <Dropdown.Item
                            as={Link}
                            to={`/app-user/${userId}/summary`}
                        >
                            <FontAwesomeIcon
                                icon={faClipboardList}
                                className="me-1"
                            />{" "}
                            Summary
                        </Dropdown.Item>
                        {isJoined && (
                            <Dropdown.Item
                                as={Link}
                                to={`/app-user/${userId}/insights`}
                            >
                                <FontAwesomeIcon
                                    icon={faTableColumns}
                                    className="me-1"
                                />{" "}
                                User Insights
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item>
                            <SendNotification
                                fetchData={fetchList}
                                user={user}
                                userId={userId}
                            />
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        {(userEmail || userPhone) && (
                            <Dropdown.Item
                                className="text-warning"
                                onClick={handleReleaseLoginIdentifiers}
                            >
                                <FontAwesomeIcon
                                    icon={faEdit}
                                    className="me-1"
                                />{" "}
                                Release login details
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item
                            className="text-danger"
                            onClick={handleDelete}
                        >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />{" "}
                            Delete
                        </Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default memo(UserActionsDropdown);
